const darkTheme = {
  name: 'dark',
  colors: {
    // Legacy properties for backward compatibility
    // These match the exact paths existing components expect
    primary: '#fa4616',
    primaryRgb: 'rgb(250, 70, 22)',
    primaryOpacity: (opacity) =>
      typeof opacity === 'number'
        ? `rgba(250, 70, 22, ${opacity})`
        : 'rgb(250, 70, 22)',
    secondary: '#00001f', // Keep original for existing components
    black: '#000',
    blueBlack: '#000033', // Same as light theme for consistency
    white: '#fff',
    border: 'rgba(255, 255, 255, 0.1)',
    'border-light': 'rgba(255, 255, 255, 0.08)',
    'border-input': 'rgba(255, 255, 255, 0.15)',
    'border-button': 'rgba(255, 255, 255, 0.15)',
    errorBackground: 'rgba(237, 67, 55, 0.2)',
    error: '#ED4337',
    success: '#22C55E', // Green used throughout app
    warning: '#FF9800',
    info: '#2196F3',

    // Background colors with gradient
    background: {
      primary: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)',
      secondary: '#1a1a2e',
      tertiary: '#16213e',
      light: '#2a2a3e',
      dark: '#0f0f23',
      mobile: '#0f0f23',
      card: 'rgba(255, 255, 255, 0.05)',
      overlay: 'rgba(255, 255, 255, 0.03)',
      gradient: {
        start: '#0f0f23',
        middle: '#1a1a2e',
        end: '#16213e',
      },
    },

    // Uniform card styling for dark theme
    card: {
      background: 'rgba(255, 255, 255, 0.05)',
      border: 'rgba(255, 255, 255, 0.1)',
      shadow: 'rgba(0, 0, 0, 0.2) 0px 1px 3px',
      shadowHover: 'rgba(0, 0, 0, 0.4) 0px 4px 12px',
      // Additional variations for consistency
      backgroundHover: 'rgba(255, 255, 255, 0.08)',
      backgroundActive: 'rgba(255, 255, 255, 0.06)',
      borderHover: 'rgba(255, 255, 255, 0.15)',
      borderActive: 'rgba(255, 255, 255, 0.2)',
    },

    // Uniform interactive element styling for dark theme
    interactive: {
      // For subtle hover effects on rows/items
      hoverBackground: 'rgba(255, 255, 255, 0.03)',
      activeBackground: 'rgba(255, 255, 255, 0.06)',
      // For buttons
      buttonHoverBackground: 'rgba(255, 255, 255, 0.1)',
      buttonActiveBackground: 'rgba(255, 255, 255, 0.12)',
    },

    // Text colors for dark theme
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.85)',
      secondaryMuted: '#94A3B8', // Lighter secondary text for better readability
      dark: '#ffffff',
      light: 'rgba(255, 255, 255, 0.7)',
      black: '#ffffff',
      link: '#fa4616',
      white: '#ffffff',
      gray: 'rgba(255, 255, 255, 0.6)',
      header: '#ffffff',
      muted: 'rgba(255, 255, 255, 0.5)',
    },

    // Gray scale adapted for dark theme
    gray: {
      50: '#1a1a1a',
      100: '#2a2a2a',
      200: '#3a3a3a',
      300: '#4a4a4a',
      400: '#5a5a5a',
      500: '#6a6a6a',
      600: '#7a7a7a',
      700: '#8a8a8a',
      800: '#9a9a9a',
      900: '#aaaaaa',
      A100: '#333333',
      A200: '#555555',
      A400: '#777777',
      A700: '#999999',
    },

    // Analytics specific colors for dark theme
    analytics: {
      cardBg: 'rgba(255, 255, 255, 0.05)',
      cardBorder: 'rgba(255, 255, 255, 0.1)',
      cardShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
      statValue: '#ffffff',
      statLabel: 'rgba(255, 255, 255, 0.7)',
      chartPrimary: '#fa4616',
      chartSecondary: '#22C55E', // Green
      chartTertiary: '#2196F3',
      chartQuaternary: '#FF9800',
      positive: '#22C55E', // Green for positive values
      negative: '#ED4337',
      neutral: 'rgba(255, 255, 255, 0.5)',
      // Special effects for dark theme
      glowPrimary: '0 0 20px rgba(250, 70, 22, 0.3)',
      glowSuccess: '0 0 20px rgba(34, 197, 94, 0.3)', // Updated to match green
      backdropBlur: 'blur(10px)',
    },

    // Segment colors (same for both light and dark themes)
    segments: {
      superFan: '#22C55E', // Green - Super Fan
      regularFan: '#3B82F6', // Blue - Regular Fan
      casualFan: '#06B6D4', // Cyan - Casual Fan
      firstTimer: '#F59E0B', // Orange - First-Timer
      inactive: '#DC2626', // Red - Inactive
    },

    // Status colors for toasters and alerts
    status: {
      success: '#22C55E', // Green - Same as superFan
      error: '#EF4444', // Red - Error primary
      errorAlt: '#DC2626', // Red - Error alt/gradient (same as inactive)
      info: '#3B82F6', // Blue - Same as regularFan
    },

    // Event status colors
    eventStatus: {
      draft: {
        background: 'rgba(148, 163, 184, 0.15)',
        text: '#94a3b8',
        border: 'rgba(148, 163, 184, 0.3)',
      },
      published: {
        background: 'rgba(34, 197, 94, 0.15)',
        text: '#4ade80',
        border: 'rgba(34, 197, 94, 0.3)',
      },
      postponed: {
        background: 'rgba(234, 179, 8, 0.15)',
        text: '#facc15',
        border: 'rgba(234, 179, 8, 0.3)',
      },
      cancelled: {
        background: 'rgba(239, 68, 68, 0.15)',
        text: '#f87171',
        border: 'rgba(239, 68, 68, 0.3)',
      },
    },

    // Standardized gray text colors for dark theme (inverse of light theme)
    textGray: {
      primary: '#ffffff', // White - main text (highest contrast)
      dark: '#e2e8f0', // Very light gray - emphasized/important text
      secondary: '#94a3b8', // Light gray-blue - secondary/body text
      tertiary: '#7c8a9d', // Medium-light gray - between secondary and muted
      muted: '#64748b', // Medium gray - muted/subtle text
      disabled: '#475569', // Dark gray - disabled states (lowest contrast)
    },
  },

  // Typography
  fonts: {
    primary:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    secondary:
      '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    monospace: '"SF Mono", Monaco, Consolas, "Courier New", monospace',
    // Required font definitions for Text component
    default: {
      fontSize: 14,
      lineHeight: 24,
      fontWeight: 400,
    },
    heading: {
      fontSize: 24,
      lineHeight: 28,
      fontWeight: 600,
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    label: {
      fontSize: 16,
      lineHeight: 19,
      fontWeight: 500,
    },
    bold: {
      fontSize: 20,
      lineHeight: 24,
      fontWeight: 600,
    },
    body: {
      fontSize: 24,
      lineHeight: 28,
      fontWeight: 800,
    },
    body4: {
      fontSize: 14,
      lineHeight: 18,
      fontWeight: 400,
    },
  },

  // Spacing (same as light theme)
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },

  // Border radius (same as light theme)
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    round: '50%',
  },

  // Transitions
  transitions: {
    fast: '150ms ease-in-out',
    normal: '250ms ease-in-out',
    slow: '350ms ease-in-out',
    theme: '300ms ease-in-out', // Special transition for theme switching
  },

  // Shadows adapted for dark theme
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.3)',
    md: '0 2px 8px rgba(0, 0, 0, 0.4)',
    lg: '0 4px 16px rgba(0, 0, 0, 0.5)',
    xl: '0 8px 32px rgba(0, 0, 0, 0.6)',
    glow: '0 0 20px rgba(250, 70, 22, 0.2)', // Glow effect for dark theme
  },
};

export default darkTheme;
